/**
 * 20261008 (owner): Audio ▸ Create credit rule — the estimate quotes what the server deducts.
 *
 * Source of truth: supabase/functions/ai-generate/_tts.ts (TTS_COSTS + AUDIO_GEN_COSTS),
 * mirrored by the client TTS_COSTS in types/tts.ts (pinned by __tests__/scratchCredits.test.ts):
 *   - submit-tts (ONE line)                         → perLine
 *   - submit-tts-scene / submit-tts-batch (a batch) → a FLAT perScene per call, whatever the
 *     number of lines in it (refunded only when every line in the batch fails)
 *   - submit-audio-gen (Music / SFX)                → flat per clip
 *
 * What ScratchPanel's Generate button actually sends, per scope:
 *   - Dialogue · Custom, and Voiceover → generateSingleTts, one call      → perLine
 *   - Dialogue · Selected Shot         → generateSceneTts, ONE batch      → perScene (even for 1 line)
 *   - Dialogue · Selected Scene        → generateSceneTts, ONE batch      → perScene
 *   - Dialogue · All Scenes            → generateSceneTts once PER SCENE that has dialogue lines
 *                                        → perScene × that scene count
 *   - Music / SFX                      → generateAudio, one clip          → flat clip price
 *
 * The estimate is an upper bound: with "Overwrite existing dialogue" off or "Skip approved
 * dialogue" on, a shot/scene whose lines are all filtered out is skipped and not billed.
 */
import { TTS_COSTS } from '@/types/tts.ts';

export interface ScratchCreditInput {
  /** 'dialogue' | 'voiceover' | 'music' | 'sfx' */
  scratchType: string;
  /** 'allScenes' | 'selectedScene' | 'selectedShot' | 'custom' */
  scratchScope: string;
  /** TTS model id (kokoro | elevenlabs). Ignored for Music / SFX. */
  model: string;
  /** Lines the scope covers (1 for a typed line or a music/SFX prompt); 0 = nothing to send. */
  lineCount: number;
  /** All Scenes only: scenes holding at least one dialogue line — each is sent as one batch. */
  scenesWithDialogue: number;
}

export interface ScratchCreditPlan {
  /** Price of ONE request in this scope: a line, a batch, or a clip. */
  unitCost: number;
  /** Requests the Generate button will send (0 = nothing to send). */
  requests: number;
  /** unitCost × requests — the credits the server will deduct. */
  total: number;
}

/** Script dialogue (every Dialogue scope except Custom) goes out as batches, billed per batch. */
export function isBatchDialogueScope(scratchType: string, scratchScope: string): boolean {
  return scratchType === 'dialogue' && scratchScope !== 'custom';
}

export function scratchUnitCost(scratchType: string, scratchScope: string, model: string): number {
  if (scratchType === 'music' || scratchType === 'sfx') {
    // Music / SFX keep their flat clip price in the perLine slot (types/tts.ts).
    return TTS_COSTS[scratchType]?.perLine ?? (scratchType === 'music' ? 40 : 5);
  }
  // Unknown model → the dearer table, so the estimate never under-quotes.
  const costs = TTS_COSTS[model] ?? TTS_COSTS.elevenlabs;
  return isBatchDialogueScope(scratchType, scratchScope) ? costs.perScene : costs.perLine;
}

export function planScratchCredits(input: ScratchCreditInput): ScratchCreditPlan {
  const unitCost = scratchUnitCost(input.scratchType, input.scratchScope, input.model);
  let requests = 0;
  if (input.lineCount > 0) {
    requests = input.scratchType === 'dialogue' && input.scratchScope === 'allScenes'
      ? Math.max(0, input.scenesWithDialogue)
      : 1;
  }
  return { unitCost, requests, total: unitCost * requests };
}

/** The server's returned token_cost when it is a usable number, else the rule's fallback. */
export function chargedCredits(serverTokenCost: unknown, fallback: number): number {
  return typeof serverTokenCost === 'number' && Number.isFinite(serverTokenCost) && serverTokenCost >= 0
    ? serverTokenCost
    : fallback;
}

/**
 * Spread one batch's charge over the clips it produced, in whole credits that sum EXACTLY to
 * the charge (10 over 3 → [4, 3, 3]), so per-clip history entries total what was deducted.
 */
export function splitCredits(total: number, count: number): number[] {
  if (count <= 0) return [];
  const whole = Math.max(0, Math.round(total));
  const base = Math.floor(whole / count);
  const remainder = whole - base * count;
  return Array.from({ length: count }, (_, i) => base + (i < remainder ? 1 : 0));
}
